import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAccount } from '@core/utils/auth';
import { usePreferences } from '@core/utils/prefs';
import { CookieConsentModal } from './CookieConsentModal';
import { readConsent, subscribeConsent, writeConsent, type AnalyticsConsent } from './consent';
import {
  clearUser,
  disableAnalytics,
  identifyUser,
  initAnalytics,
  isAnalyticsConfigured,
  listenClicks,
  syncDocumentTitle,
  track,
  trackPageView,
} from './client';
import { GA } from './events';

function persistConsent(
  value: AnalyticsConsent,
  sub: string | undefined,
  updatePrefs: (partial: { analyticsConsent: AnalyticsConsent }) => Promise<void>,
): void {
  writeConsent(value, sub);
  if (sub) void updatePrefs({ analyticsConsent: value });
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user } = useAccount();
  const { prefs, prefsReady, updatePrefs } = usePreferences();
  const printMode = new URLSearchParams(location.search).get('print') === '1';
  const [consent, setConsent] = useState(() => readConsent(user?.sub));
  const configured = isAnalyticsConfigured();
  const waitingForSettings = Boolean(user) && !prefsReady;
  const enabled = configured && consent === 'granted' && !printMode && !waitingForSettings;

  useEffect(() => subscribeConsent(setConsent), []);

  useEffect(() => {
    setConsent(readConsent(user?.sub));
  }, [user?.sub]);

  useEffect(() => {
    if (prefs?.analyticsConsent) writeConsent(prefs.analyticsConsent, user?.sub);
  }, [prefs?.analyticsConsent, user?.sub]);

  useEffect(() => {
    if (enabled) initAnalytics();
    else disableAnalytics();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (user) identifyUser(user);
    else clearUser();
  }, [user, enabled]);

  useEffect(() => {
    syncDocumentTitle(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!enabled) return;
    trackPageView(location.pathname);
  }, [location.pathname, enabled]);

  useEffect(() => {
    if (!enabled) return;
    return listenClicks();
  }, [enabled]);

  return (
    <>
      <CookieConsentModal
        open={configured && !printMode && !waitingForSettings && consent == null}
        onAccept={() => {
          persistConsent('granted', user?.sub, updatePrefs);
          initAnalytics();
          track(GA.acceptedAnalytics);
        }}
        onDecline={() => {
          persistConsent('denied', user?.sub, updatePrefs);
          disableAnalytics();
        }}
      />
      {children}
    </>
  );
}
